/**
 * Clarity integration wire types and tolerant decoders for the admin app.
 *
 * Decoders are hand-written pure functions (not RTK `responseSchema`): a failed
 * decode returns `null` and never carries the raw body anywhere, so a token
 * response can never leak through a schema error into logs or devtools.
 * Extra keys are ignored; a missing or mistyped required key yields `null`.
 */

/** `ConnectionResource`; older backends may omit `clarity_organization_id`. */
export interface ClarityConnection {
  organization_id: number;
  enabled: boolean;
  external_organization_id: string | null;
  clarity_organization_id: string | null;
  public_origins: string[];
  has_token: boolean;
  has_previous_token: boolean;
  token_rotated_at: string | null;
  /** `null` only for the unsaved default projection (no stored row yet). */
  updated_at: string | null;
  unprepared_file_count: number;
  unresolvable_file_count: number;
  widgets_ready_count: number;
  widgets_not_ready_count: number;
}

/** Public-origin settings; the backend owns external organization IDs. */
export interface ClarityConnectionUpdate {
  enabled: boolean;
  public_origins?: string[];
}

export interface ClarityLookup {
  clarity_organization_id: string;
  name: string;
  connected: boolean;
  connected_to_this_organization: boolean;
}

export interface ClarityProfileOptIn {
  profileId: number;
  /** `null` when the API did not return a boolean `clarity_chatbot_enabled` (not SuperGod or backend not deployed). */
  enabled: boolean | null;
}

export const CLARITY_TOKEN_PREFIX = 'wpclr_';
export const MAX_PUBLIC_ORIGINS = 100;
export const MAX_ORIGIN_LENGTH = 2048;
export const MAX_CLARITY_ID_LENGTH = 128;

type JsonObject = Record<string, unknown>;

const isObject = (v: unknown): v is JsonObject => typeof v === 'object' && v !== null && !Array.isArray(v);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const isNullableString = (v: unknown): v is string | null => v === null || typeof v === 'string';

export function decodeClarityConnection(v: unknown): ClarityConnection | null {
  if (!isObject(v)) return null;
  const {
    organization_id, enabled, external_organization_id, public_origins, has_token, has_previous_token,
    token_rotated_at, updated_at, unprepared_file_count, unresolvable_file_count, widgets_ready_count,
    widgets_not_ready_count,
  } = v;
  const clarity_organization_id = Object.hasOwn(v, 'clarity_organization_id') ? v.clarity_organization_id : null;
  if (!isNullableString(clarity_organization_id)) return null;
  if (typeof organization_id !== 'number' || !Number.isInteger(organization_id)) return null;
  if (typeof enabled !== 'boolean' || typeof has_token !== 'boolean' || typeof has_previous_token !== 'boolean') return null;
  if (!isNullableString(external_organization_id) || !isNullableString(token_rotated_at) || !isNullableString(updated_at)) return null;
  if (!Array.isArray(public_origins) || !public_origins.every((o) => typeof o === 'string')) return null;
  if (![unprepared_file_count, unresolvable_file_count, widgets_ready_count, widgets_not_ready_count].every(isCount)) return null;
  return {
    organization_id,
    enabled,
    external_organization_id,
    clarity_organization_id,
    public_origins: [...(public_origins as string[])],
    has_token,
    has_previous_token,
    token_rotated_at,
    updated_at,
    unprepared_file_count: unprepared_file_count as number,
    unresolvable_file_count: unresolvable_file_count as number,
    widgets_ready_count: widgets_ready_count as number,
    widgets_not_ready_count: widgets_not_ready_count as number,
  };
}

/** Lookup data is bound to the checked ID; extra fields (including external IDs) are discarded. */
export const decodeClarityLookup = (expectedId: string) => (v: unknown): ClarityLookup | null => {
  if (!isObject(v)) return null;
  const { clarity_organization_id, name, connected, connected_to_this_organization } = v;
  if (clarity_organization_id !== expectedId) return null;
  if (typeof name !== 'string' || name.trim().length === 0 || name.length > 256) return null;
  if (typeof connected !== 'boolean' || typeof connected_to_this_organization !== 'boolean') return null;
  return { clarity_organization_id: expectedId, name, connected, connected_to_this_organization };
};

/** Reads the admin profile body (`GET/PATCH /admin/api/profiles/{id}`). */
export function decodeClarityProfileOptIn(v: unknown): ClarityProfileOptIn | null {
  if (!isObject(v)) return null;
  const { id, clarity_chatbot_enabled } = v;
  if (typeof id !== 'number' || !Number.isInteger(id)) return null;
  return { profileId: id, enabled: typeof clarity_chatbot_enabled === 'boolean' ? clarity_chatbot_enabled : null };
}

export function normalizeClarityOrganizationId(raw: string): { value: string } | { error: string } {
  const value = raw.trim();
  if (value === '') return { error: 'Enter a Clarity organization ID.' };
  if (value.length > MAX_CLARITY_ID_LENGTH) return { error: `Use at most ${MAX_CLARITY_ID_LENGTH} characters.` };
  if (!/^[A-Za-z0-9._:-]+$/.test(value)) {
    return { error: 'Use only letters, digits, dot, underscore, colon and hyphen.' };
  }
  return { value };
}

/** https scheme (any case; the server lowercases), a host (optionally with a port), and nothing else: no path (not even `/`), query, fragment, userinfo or whitespace. */
const ORIGIN_PATTERN = /^https:\/\/[^\s/?#@\\]+$/i;

/**
 * Splits the textarea into trimmed non-empty lines and checks each one.
 * `errors` is keyed by the index into `origins`, so line N+1 on screen equals
 * index N once the textarea is rewritten to the normalized lines.
 * The server canonicalizes the rest (lowercase, default port, de-duplication, sorting).
 */
export function normalizeOrigins(text: string): { origins: string[]; errors: Record<number, string> } {
  const origins = text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== '');
  const errors: Record<number, string> = {};
  if (origins.length > MAX_PUBLIC_ORIGINS) {
    errors[MAX_PUBLIC_ORIGINS] = `At most ${MAX_PUBLIC_ORIGINS} origins are allowed.`;
    return { origins, errors };
  }
  origins.forEach((line, index) => {
    if (line.length > MAX_ORIGIN_LENGTH) {
      errors[index] = `Use at most ${MAX_ORIGIN_LENGTH} characters.`;
      return;
    }
    let protocol: string | null = null;
    try {
      protocol = new URL(line).protocol;
    } catch {
      protocol = null;
    }
    if (!ORIGIN_PATTERN.test(line) || protocol !== 'https:') {
      errors[index] = 'Enter an https origin such as https://www.example.org (no path, query or trailing slash).';
    }
  });
  return { origins, errors };
}

export interface ClarityErrorDescription {
  fields?: Record<string, string>;
  provisioning?: { kind: 'rejected' | 'unavailable'; code: string };
  message?: string;
}

export const GENERIC_CLARITY_ERROR = 'Could not reach the server. Try again.';
export const CLARITY_REJECTION_HINTS: Record<string, string> = {
  organization_not_found: 'Check the Clarity organization ID.',
  organization_already_connected: 'This Clarity organization is already connected to another WellPrept organization. Check the Clarity organization ID.',
  external_organization_in_use: 'This WellPrept organization is still linked to another Clarity organization. Ask Clarity operations to release it, then connect again.',
  external_organization_mismatch: 'The Clarity organization is linked to a different WellPrept organization. Nothing was disconnected; contact Clarity operations.',
  catalog_host_not_allowed: "Clarity does not accept this environment's catalog address. Contact Clarity operations.",
  catalog_base_url_invalid: "Clarity does not accept this environment's catalog address. Contact Clarity operations.",
};

export function clarityRejectionHint(code: string): string | null {
  return Object.hasOwn(CLARITY_REJECTION_HINTS, code) ? CLARITY_REJECTION_HINTS[code] : null;
}

const ORIGIN_LINE_KEY = /^public_origins\.(\d+)$/;

const stringMessages = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((m): m is string => typeof m === 'string') : [];

/** Laravel 422 `errors` → one UI string per field; `public_origins.N` is labelled with its visible line. */
function validationFields(errors: Record<string, unknown>): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(errors)) {
    const messages = stringMessages(value);
    if (messages.length === 0) continue;
    const line = ORIGIN_LINE_KEY.exec(key);
    fields[key] = line ? `Line ${Number(line[1]) + 1}: ${messages.join(' ')}` : messages.join(' ');
  }
  return fields;
}

/** Maps an RTK Query error to UI text. Returns only validation strings or a validated provisioning code from the body. */
export function describeClarityError(error: unknown): ClarityErrorDescription {
  if (!isObject(error) || !isObject(error.data)) return { message: GENERIC_CLARITY_ERROR };
  const { status, data } = error;
  if (status === 422 && isObject(data.errors)) {
    const fields = validationFields(data.errors);
    if (Object.keys(fields).length > 0) return { fields };
  }
  if ((status === 409 || status === 502)
    && (data.error === 'clarity_rejected' || data.error === 'clarity_unavailable')
    && typeof data.code === 'string' && /^[a-z0-9_]{1,64}$/.test(data.code)) {
    return { provisioning: { kind: data.error === 'clarity_rejected' ? 'rejected' : 'unavailable', code: data.code } };
  }
  return { message: GENERIC_CLARITY_ERROR };
}
